import React, { createContext, useContext, useEffect, useState } from "react";
import { getLocalCache } from "utils/localCache";
// This file defines the context for managing layout state in a React application.

// It provides a way to manage user roles (like "user" or "admin") and allows components to access and modify this state.
type RoleType = "user" | "admin";

// LayoutContextType defines the shape of the context that will be provided to components.
// It includes the current role and a function to set the role.
interface LayoutContextType {
  role: RoleType;
  setRole: (role: RoleType) => void;
}

// Create a context for the layout with an initial value of undefined.
// This context will be used to provide the current role and a function to change it.
const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// LayoutProvider component
// This component provides the layout context to its children, allowing them to access and modify the current role.
export const LayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRole] = useState<RoleType>("user");

  useEffect(() => {
    // This effect runs once when the component mounts.
    // It can be used to perform any setup needed for the layout context.

    const cachedRole = getLocalCache<RoleType>("role");
    if (cachedRole !== null && cachedRole !== undefined) {
      setRole(cachedRole);
    } else {
      // If no cached role is found, set the default role to "user".
      setRole("user");
    }
  }, [role]);

  return (
    <LayoutContext.Provider value={{ role, setRole }}>
      {children}
    </LayoutContext.Provider>
  );
};

// useLayout hook
// This hook allows components to access the layout context without needing to use the context API directly.
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error("useLayout must be used within LayoutProvider");
  return context;
};
